"use client"
import Link from "next/link"
import Image from "next/image"
import { useState } from "react"
import { useRouter } from "next/navigation"

export default function ForgotPasswordPage() {
  const router = useRouter()
  const [step, setStep] = useState<"phone" | "otp" | "password">("phone")
  const [phone, setPhone] = useState("")
  const [otp, setOtp] = useState("")
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [resetToken, setResetToken] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [devOtp, setDevOtp] = useState("")

  async function handleSendOTP() {
    if (!phone) { setError("Please enter your phone number"); return }
    setLoading(true); setError("")
    try {
      const res = await fetch("/api/auth/otp", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone }) })
      const data = await res.json()
      if (data.success) { setStep("otp"); if (data.devOtp) setDevOtp(data.devOtp) }
      else setError(data.error ?? "Failed to send code")
    } catch { setError("Something went wrong. Please try again.") }
    setLoading(false)
  }

  async function handleVerifyOTP() {
    if (otp.length !== 6) { setError("Please enter the 6-digit code"); return }
    setLoading(true); setError("")
    try {
      const res = await fetch("/api/auth/verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone, otp, mode: "reset" }) })
      const data = await res.json()
      if (data.success) { setResetToken(data.resetToken); setStep("password") }
      else setError(data.error ?? "Incorrect code")
    } catch { setError("Something went wrong. Please try again.") }
    setLoading(false)
  }

  async function handleSetPassword() {
    if (password.length < 8) { setError("Password must be at least 8 characters"); return }
    if (password !== confirm) { setError("Passwords do not match"); return }
    setLoading(true); setError("")
    try {
      const res = await fetch("/api/auth/set-password", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password, resetToken }) })
      const data = await res.json()
      if (data.success) router.push("/auth/login?reset=1")
      else setError(data.error ?? "Failed to set password")
    } catch { setError("Something went wrong. Please try again.") }
    setLoading(false)
  }

  const titles: Record<typeof step, string> = { phone: "Reset password", otp: "Enter your code", password: "Set new password" }
  const subs: Record<typeof step, string> = {
    phone: "Enter your Zimbabwe phone number. We will send a verification code.",
    otp: `We sent a 6-digit code to +263${phone}.`,
    password: "Choose a new password for your account.",
  }

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--cream)", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 440 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <Link href="/" style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 32, fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 600, color: "var(--navy)", textDecoration: "none" }}>
            <Image src="/icon-full-color.svg" width={36} height={36} alt="" style={{ background: "var(--cream-warm)", borderRadius: 8, padding: 4, flexShrink: 0, display: "block" }} />
            Zim <span style={{ color: "var(--gold)" }}>Estates</span>
          </Link>
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 32, fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>{titles[step]}</h1>
          <p style={{ color: "var(--muted)", fontSize: 15 }}>{subs[step]}</p>
        </div>

        <div style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 20, padding: 32, boxShadow: "var(--shadow-lg)" }}>
          {step === "phone" && (
            <>
              <div style={{ marginBottom: 20 }}>
                <label htmlFor="fp-phone" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Zimbabwe phone number</label>
                <div style={{ display: "flex" }}>
                  <div style={{ padding: "12px 14px", border: "1px solid var(--line)", borderRight: "none", borderRadius: "8px 0 0 8px", fontSize: 14, color: "var(--navy)", fontWeight: 600, background: "var(--cream-warm)", display: "flex", alignItems: "center" }}>+263</div>
                  <input id="fp-phone" value={phone} onChange={e => setPhone(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSendOTP()} placeholder="77 234 5678" type="tel" inputMode="numeric" autoComplete="tel" style={{ flex: 1, padding: "12px 14px", border: "1px solid var(--line)", borderRadius: "0 8px 8px 0", fontSize: 14, fontFamily: "inherit", background: "var(--cream)", color: "var(--navy)", outline: "none" }} />
                </div>
              </div>
              {error && <div role="alert" style={{ fontSize: 13, color: "var(--red-text)", padding: "8px 12px", background: "rgba(196,68,68,0.06)", borderRadius: 8, marginBottom: 16 }}>{error}</div>}
              <button onClick={handleSendOTP} disabled={loading} style={{ width: "100%", padding: 14, background: loading ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {loading ? "Sending..." : "Send verification code"}
              </button>
            </>
          )}

          {step === "otp" && (
            <>
              {devOtp && <div style={{ background: "rgba(200,150,62,0.1)", border: "1px solid rgba(200,150,62,0.3)", borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: "var(--gold-deep)", textAlign: "center" }}>Sandbox mode, your code is: <strong>{devOtp}</strong></div>}
              <div style={{ marginBottom: 20 }}>
                <label htmlFor="fp-otp" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>6-digit verification code</label>
                <input id="fp-otp" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").substring(0, 6))} onKeyDown={e => e.key === "Enter" && handleVerifyOTP()} placeholder="000000" maxLength={6} type="text" inputMode="numeric" autoComplete="one-time-code" style={{ width: "100%", padding: "16px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 32, fontFamily: "Fraunces, serif", fontWeight: 600, textAlign: "center", letterSpacing: 12, background: "var(--cream)", color: "var(--navy)", outline: "none" }} />
              </div>
              {error && <div role="alert" style={{ fontSize: 13, color: "var(--red-text)", padding: "8px 12px", background: "rgba(196,68,68,0.06)", borderRadius: 8, marginBottom: 16 }}>{error}</div>}
              <button onClick={handleVerifyOTP} disabled={loading} style={{ width: "100%", padding: 14, background: loading ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit", marginBottom: 16 }}>
                {loading ? "Verifying..." : "Verify code"}
              </button>
              <div style={{ textAlign: "center", fontSize: 13, color: "var(--muted)" }}>
                <button onClick={() => { handleSendOTP(); setOtp("") }} disabled={loading} style={{ color: "var(--gold-deep)", fontWeight: 600, background: "none", border: "none", cursor: loading ? "not-allowed" : "pointer", fontSize: 13, fontFamily: "inherit" }}>Resend code</button>
                {" · "}
                <button onClick={() => { setStep("phone"); setOtp(""); setError("") }} style={{ color: "var(--muted)", fontWeight: 600, background: "none", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>Change number</button>
              </div>
            </>
          )}

          {step === "password" && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="fp-password" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>New password</label>
                <input id="fp-password" value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="At least 8 characters" autoComplete="new-password" style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", color: "var(--navy)", outline: "none" }} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label htmlFor="fp-confirm" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Confirm password</label>
                <input id="fp-confirm" value={confirm} onChange={e => setConfirm(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSetPassword()} type="password" placeholder="Repeat your password" autoComplete="new-password" style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", color: "var(--navy)", outline: "none" }} />
              </div>
              {error && <div role="alert" style={{ fontSize: 13, color: "var(--red-text)", padding: "8px 12px", background: "rgba(196,68,68,0.06)", borderRadius: 8, marginBottom: 16 }}>{error}</div>}
              <button onClick={handleSetPassword} disabled={loading} style={{ width: "100%", padding: 14, background: loading ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {loading ? "Saving..." : "Set new password"}
              </button>
            </>
          )}
        </div>

        <div style={{ textAlign: "center", marginTop: 24, fontSize: 13, color: "var(--muted)" }}>
          <Link href="/auth/login" style={{ color: "var(--gold-deep)", fontWeight: 600 }}>Back to log in</Link>
        </div>
      </div>
    </div>
  )
}
